import _ from 'lodash'
import { useMemo } from 'react'

import { AudioRecordingData } from '@/components/ui/tables/AudioRecordingTable'

import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import { useAppSelector } from '@/redux/rootReducer'
import { selectConsultationById, selectConsultationUpdateRequestTracker, updateConsultation } from '@/redux/reducers/consultation.reducer'
import {
  deleteRecordingAnnotation,
  selectRecordingAnnotationDeleteRequestTracker,
  selectRecordingAnnotationUpdateRequestTracker,
  updateRecordingAnnotation,
} from '@/redux/reducers/document.reducer'

import VitalData from '../components/VitalData'
import { useGetConsultationQuery, useUpdateConsultationMutation } from '@/core/services/consultation'
import { skipToken } from '@reduxjs/toolkit/query'
import { getApiErrorMessage } from '@/utils/errors'

type VitalDataContainerProps = {
  consultationId: string
}

export default function VitalDataContainer(props: VitalDataContainerProps) {
  const { consultationId } = props
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  // TODO: remove, but keep it for backward compatibility
  const consultation = useAppSelector((state) => selectConsultationById(state, consultationId))

  const mesures = useMemo(
    () =>
      _.map(consultation?.mesures, (mesure) => ({
        label: mesure.label,
        order: mesure.order,
        unit: mesure.unit,
        value: mesure.value?.toString() || '',
      })),
    [consultation]
  )

  const recordingFiles = useMemo(
    () =>
      _.map(
        _.filter(consultation?.files, (file) => {
          // backward compatibility for files which start with audio and end with corresponding extension
          // TODO: keep only file.isRecording
          return (file.filename.toLowerCase().startsWith('audio') && file.filename.toLowerCase().endsWith('mp3')) || !!file.isRecording
        }),
        (file) => ({
          annotations: file.recordingAnnotations,
          filename: file.filename,
          key: file.key,
          url: file.url,
        })
      ),
    [consultation]
  )

  const biologicalTests = useMemo(() => {
    if (!consultation) return []
    return _.map(consultation.biologicals, (b) => ({
      ...b,
      file: consultation.files.find((e) => e.key === b.file),
    }))
  }, [consultation])

  const deleteRecordingAnnotationRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.recordingAnnotation.delete.success'), { variant: 'success' })
    },
    onError: () => {
      enqueueSnackbar(t('notification.recordingAnnotation.delete.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectRecordingAnnotationDeleteRequestTracker,
  })

  const updateRecordingAnnotationRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.recordingAnnotation.save.success'), { variant: 'success' })
    },
    onError: () => {
      enqueueSnackbar(t('notification.recordingAnnotation.save.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectRecordingAnnotationUpdateRequestTracker,
  })

  const handleDeleteAnnotation = (annotationId: string, recordingFile: AudioRecordingData) => {
    if (consultationId) {
      deleteRecordingAnnotationRequestHandler.dispatch(deleteRecordingAnnotation(recordingFile.key, annotationId, 'consultation', consultationId))
    }
  }

  const handleUpdateAnnotation = (annotation: { id: string; start: number; end: number; note: string }, recordingFile: AudioRecordingData) => {
    if (consultationId) {
      updateRecordingAnnotationRequestHandler.dispatch(updateRecordingAnnotation(recordingFile.key, annotation, 'consultation', consultationId))
    }
  }

  const [updateConsultationMutation] = useUpdateConsultationMutation()

  const handleCommentChange = async (comment: string) => {
    if (consultationId) {
      try {
        await updateConsultationMutation({ id: consultationId, changes: { commentaire: comment } }).unwrap()
        enqueueSnackbar(t('notification.consultation.update.success'), { variant: 'success' })
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.update.error')), { variant: 'error' })
      }
    }
  }

  return (
    <VitalData
      comment={consultation?.commentaire || ''}
      commentEditing={true}
      onCommentChange={handleCommentChange}
      mesures={mesures}
      onDeleteAnnotation={handleDeleteAnnotation}
      onUpdateAnnotation={handleUpdateAnnotation}
      recordingFiles={recordingFiles}
      biologicalTests={biologicalTests}
    />
  )
}
